import { useEffect, useMemo } from 'react';
import { getWorkflowType, getStartNode } from 'modules/workflows/ducks';
import { useSelector } from 'react-redux';
import { Link } from 'ui/links';
import { Body2 } from 'ui/theme';
import { TipAlign, Tooltip } from 'ui/tooltip';
import {
  PopoverContentWrapper,
  VistorTrackingBodySecondary,
  PublishedJourneyOptionsContainer,
  StyledToggleField,
  PublishedJourneyTitleContainer,
  SwitchLabel,
  StyledClickTip,
  VisitorTrackingWarning,
  VistorTrackingBodyPrimary,
  VisitorTrackingBodyBold,
  StyledLoadingInfoContainer,
} from './PublishedJourneyOptions.styled';
import { useGetJourneyMetricUsage } from '../../hooks/useGetJourneyMetricUsage';
import { useCalculateScheduledJourneyListUserCount } from './useCalculateScheduledJourneyListUserCount';
import { getJourneyUsageProjectections } from './publishedJourneyOptions.utils';
import { useJourneyVisitorTrackingContext } from 'modules/workflows/context/JourneyVisitorTrackingContext';
import { CircleSpinner } from 'ui/spinner';

export const JOURNEY_USAGE_WARNING_TEST_ID = 'visitor-usage-tracking-warning';
export const JOURNEY_USAGE_PRIMARY_TEST_ID = 'visitor-usage-tracking-primary';
export const JOURNEY_USAGE_CONTACT_CSM_TEST_ID = 'visitor-usage-contact-csm';
export const PROJECTED_USAGE_TEST_ID = 'projected-usage';

export enum PublishedJourneyOptionsConsumer {
  JourneySettings,
  PublishJourneyModal,
}
const TooltipContent = ({ currentUsagePercentage }: { currentUsagePercentage?: number }) => {
  return (
    <PopoverContentWrapper>
      <div>
        <Body2>
          Generates events when the users exit the journey including when and why they exited and
          the path they took.
        </Body2>
        <Link
          href="https://support.iterable.com/hc/en-us/articles/10044063369492-Monitoring-Journey-Engagement"
          target="_blank"
        >
          Learn more
        </Link>
      </div>
      {currentUsagePercentage ? (
        <div>
          <VistorTrackingBodySecondary>
            {currentUsagePercentage}% of monthly plan limit used
          </VistorTrackingBodySecondary>
        </div>
      ) : null}
    </PopoverContentWrapper>
  );
};

const TOOLTIP_COPY = {
  isDraft: 'Available once the journey is published',
};

export const PublishedJourneyOptions = ({
  name = 'visitorTrackingEnabled',
  publishedJourneyOptionsConsumer,
  isDisabled = false,
}: {
  name?: string;
  publishedJourneyOptionsConsumer: PublishedJourneyOptionsConsumer;
  isDisabled?: boolean;
}) => {
  const isPublishedJourneyModalConsumer =
    publishedJourneyOptionsConsumer === PublishedJourneyOptionsConsumer.PublishJourneyModal;

  const {
    currentJourneyEligibility,
    publishedJourneyEligibility,
    checkCurrentJourneyEligibility,
    checkPublishedJourneyEligibility,
    isLoading,
  } = useJourneyVisitorTrackingContext();

  useEffect(() => {
    if (isPublishedJourneyModalConsumer) {
      checkCurrentJourneyEligibility();
    } else {
      checkPublishedJourneyEligibility();
    }
  }, []);

  const startNode = useSelector(getStartNode);
  const emailListId = startNode?.nodeData?.emailListId;
  const suppressionListIds = startNode?.nodeData?.suppressionListIds;
  const searchQueries = startNode?.nodeData?.searchCombo?.searchQueries;

  const { isPublished, draftOnly } = useSelector(getWorkflowType);

  const { isJourneyEligible, jvtEligibilityMessage } =
    publishedJourneyOptionsConsumer === PublishedJourneyOptionsConsumer.JourneySettings
      ? publishedJourneyEligibility
      : currentJourneyEligibility;

  const eligibilityMessage = jvtEligibilityMessage?.[0];

  const { userCount, getCount } = useCalculateScheduledJourneyListUserCount({
    emailListId,
    suppressionListIds,
    searchQueries,
  });
  useEffect(() => {
    if (emailListId) {
      getCount?.();
    }
  }, [emailListId, getCount]);
  const { currentUsage, currentUsagePercentage, limit } = useGetJourneyMetricUsage();

  const { projectedUsagePercentage, willLimitBeReached } = getJourneyUsageProjectections(
    limit,
    currentUsage,
    userCount,
  );

  const tooltipContent = useMemo(() => {
    if (publishedJourneyOptionsConsumer === PublishedJourneyOptionsConsumer.JourneySettings) {
      if (draftOnly) return TOOLTIP_COPY.isDraft;
      if (!isJourneyEligible) return eligibilityMessage;
    }
    if (isPublishedJourneyModalConsumer) {
      if (!isJourneyEligible) {
        return eligibilityMessage;
      }
    }
    return '';
  }, [isJourneyEligible, isPublished, publishedJourneyOptionsConsumer, jvtEligibilityMessage]);

  const isTooltipEnabled = !!tooltipContent || isDisabled;
  const isToggleDisabled = !!isTooltipEnabled || isLoading;

  return (
    <PublishedJourneyOptionsContainer>
      <Tooltip isEnabled={isTooltipEnabled} tooltipContent={tooltipContent} align={TipAlign.Start}>
        <StyledToggleField isDisabled={isToggleDisabled} id={'track-events-field'} name={name} />
      </Tooltip>
      <div>
        <PublishedJourneyTitleContainer>
          <SwitchLabel>
            {isPublishedJourneyModalConsumer ? 'Track journey' : 'Journey'} events
          </SwitchLabel>{' '}
          <StyledClickTip
            popoverContentTitle=""
            popoverContentBody={<TooltipContent currentUsagePercentage={currentUsagePercentage} />}
          />
        </PublishedJourneyTitleContainer>
        {isPublishedJourneyModalConsumer && isLoading && (
          <StyledLoadingInfoContainer>
            <CircleSpinner size={24} />{' '}
            <span>
              Checking your project's current event usage... please wait. (If you publish before
              loading is complete, event tracking will be disabled for this journey.)
            </span>
          </StyledLoadingInfoContainer>
        )}
        {willLimitBeReached && (
          <VisitorTrackingWarning data-test={JOURNEY_USAGE_WARNING_TEST_ID}>
            <VistorTrackingBodyPrimary data-test={JOURNEY_USAGE_PRIMARY_TEST_ID}>
              You&apos;ve reached{' '}
              <VisitorTrackingBodyBold
                isEnabled
                skipDelayDuration={100}
                tooltipContent={
                  <div>
                    {currentUsage.toLocaleString()}/{limit.toLocaleString()} events
                  </div>
                }
              >
                {currentUsagePercentage}%
              </VisitorTrackingBodyBold>{' '}
              of your monthly limit for tracking journey exit events.{' '}
              {emailListId && (
                <span>
                  <span>Tracking for this journey uses </span>
                  <VisitorTrackingBodyBold
                    data-test={PROJECTED_USAGE_TEST_ID}
                    isEnabled
                    skipDelayDuration={100}
                    tooltipContent={
                      <div>
                        {userCount.toLocaleString()}/{limit.toLocaleString()} events
                      </div>
                    }
                  >
                    {projectedUsagePercentage === 0 ? `< 1` : projectedUsagePercentage}%
                  </VisitorTrackingBodyBold>
                </span>
              )}
            </VistorTrackingBodyPrimary>
            <VistorTrackingBodySecondary data-test={JOURNEY_USAGE_CONTACT_CSM_TEST_ID}>
              Need to track more exit events? Contact your CSM to update your plan limit, or turn
              off tracking on some journeys to free up space for next month.
            </VistorTrackingBodySecondary>
          </VisitorTrackingWarning>
        )}
      </div>
    </PublishedJourneyOptionsContainer>
  );
};
